import { getLocalizedValue, type Locale, useI18n } from "@/lib/i18n"
import type { LocalizedString } from "@/types/sanity"
import { type FC, useEffect } from "react"
import { type Route } from "../+types/root"

type SEOProps = {
  title?: LocalizedString
  description?: LocalizedString
  image?: string
  type?: "website" | "article" | "product"
  jsonLd?: Record<string, unknown>
  noIndex?: boolean
}

export const SEO: FC<SEOProps> = ({
  title,
  description,
  image,
  type = "website",
  jsonLd,
  noIndex = false,
}) => {
  const { locale } = useI18n()

  const siteTitle = "Poremat"
  const siteDescription =
    locale === "tr"
      ? "Poremat, MOF bazlı metal organik kafesler ile hidrojen depolama, gaz depolama, karbondioksit yakalama, filtreleme, su temizleme ve sürdürülebilir malzeme teknolojileri geliştirir."
      : "Poremat develops MOF-based metal-organic frameworks for hydrogen storage, gas storage, carbon dioxide capture, filtration, water treatment, and sustainable material technologies."

  const pageTitle = getLocalizedValue(title, locale, siteTitle)
  const pageDescription = getLocalizedValue(
    description,
    locale,
    siteDescription
  )
  const fullTitle =
    typeof title !== "undefined" && title !== null
      ? `${pageTitle} | ${siteTitle}`
      : siteTitle

  const siteUrl = "https://poremat.com"
  const defaultImage = `${siteUrl}/assets/logo.png`
  const metaImage = image ?? defaultImage

  useEffect(() => {
    // Set document title
    document.title = fullTitle

    // Set meta tags
    const setMeta = (name: string, content: string, property = false): void => {
      const attr = property ? "property" : "name"
      let meta = document.querySelector(`meta[${attr}="${name}"]`)
      if (!meta) {
        meta = document.createElement("meta")
        meta.setAttribute(attr, name)
        document.head.appendChild(meta)
      }
      meta.setAttribute("content", content)
    }

    setMeta("title", fullTitle)
    setMeta("description", pageDescription)
    setMeta("og:title", fullTitle, true)
    setMeta("og:description", pageDescription, true)
    setMeta("og:type", type, true)
    setMeta("og:url", window.location.href, true)
    setMeta("og:image", metaImage, true)
    setMeta("og:locale", locale === "tr" ? "tr_TR" : "en_US", true)
    setMeta("twitter:card", "summary_large_image")
    setMeta("twitter:title", fullTitle)
    setMeta("twitter:description", pageDescription)
    setMeta("twitter:image", metaImage)

    if (noIndex) {
      setMeta("robots", "noindex, nofollow")
    }

    // JSON-LD
    if (jsonLd) {
      let script = document.querySelector('script[type="application/ld+json"]')
      if (!script) {
        script = document.createElement("script")
        script.setAttribute("type", "application/ld+json")
        document.head.appendChild(script)
      }
      script.textContent = JSON.stringify(jsonLd)
    }
  }, [
    fullTitle,
    pageDescription,
    type,
    metaImage,
    locale,
    noIndex,
    jsonLd,
  ])

  return null
}

export function getDefaultMetaDescriptors(
  locale: Locale
): Route.MetaDescriptors {
  const description =
    locale === "tr"
      ? "Poremat, MOF bazlı metal organik kafesler ile hidrojen depolama, gaz depolama, karbondioksit yakalama, filtreleme, su temizleme ve sürdürülebilir malzeme teknolojileri geliştirir."
      : "Poremat develops MOF-based metal-organic frameworks for hydrogen storage, gas storage, carbon dioxide capture, filtration, water treatment, and sustainable material technologies."
  const title =
    locale === "tr"
      ? "Poremat | Sürdürülebilir Gözenekli Malzeme Çözümleri"
      : "Poremat | Sustainable Porous Material Solutions"

  return [
    {
      name: "title",
      content: title,
    },
    {
      name: "description",
      content: description,
    },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    { property: "og:type", content: "website" },
    {
      property: "og:url",
      content: "https://poremat.com",
    },
    {
      property: "og:image",
      content: "https://poremat.com/assets/logo.png",
    },
    {
      property: "og:locale",
      content: locale === "tr" ? "tr_TR" : "en_US",
    },
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    {
      name: "twitter:image",
      content: "https://poremat.com/assets/logo.png",
    },
  ]
}
